import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';


import { SettingModelSettingType, SolveConnectorPlacementModelMode } from 'api/models';
import { useSolveConnectorPlacement } from 'api/queries/forming/forming';
import { isFramiClamp } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { activateManualSnapPlacement } from 'app/modules/wallforming/components/scenes/objects/articles/utils/manualSnapPlacementUtils';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { buildSolveConnectorPlacementRequest } from 'app/modules/wallforming/components/setting-container/connector-placement/utils/buildSolveConnectorPlacementRequest';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EMPTY_GUID } from 'shared/constants';

import type { Vector3 } from 'three';

interface ConnectorDragReference {
    position: Vector3;
}

export interface ManualConnectorDragParams {
    articleId: string;
    articleNumber: string | null;
    poiIndex: number;
    manualSnapPoiIndex?: number;
    connectorReference?: ConnectorDragReference;
}

export const usePrepareManualConnectorDrag = () => {
    const solveConnectorPlacementMutation = useSolveConnectorPlacement();

    const [searchParams] = useSearchParams();
    const sessionguid = useMemo(() => searchParams.get('formingsession') ?? EMPTY_GUID, [searchParams]);

    return useCallback(
        async ({
            articleId,
            articleNumber,
            poiIndex,
            manualSnapPoiIndex,
            connectorReference
        }: ManualConnectorDragParams) => {
            const state = usePlacementStore.getState();

            if (!isFramiClamp(articleNumber)) {
                state.clearManualPlacementData();
                return;
            }

            state.setManualPlacementDataLoading(articleId, true);

            try {
                const response = await solveConnectorPlacementMutation.mutateAsync({
                    data: buildSolveConnectorPlacementRequest({
                        state,
                        sessionId: sessionguid,
                        systemId: import.meta.env.VITE_DEFAULT_SYSTEM_ID,
                        poiIndex,
                        manualSnapPoiIndex,
                        mode: SolveConnectorPlacementModelMode.Manual,
                        selectedConnectorArticleGuid: articleId
                    })
                });

                const isInsertion = !state.waitForPlacementData;
                if (!response.accepted || (!isInsertion && !connectorReference)) {
                    state.clearManualPlacementData();
                    return;
                }

                // Existing clamps resolve from their own position; new corner insertions follow the pointer ray.
                const isActivated = activateManualSnapPlacement({
                    state,
                    articleId,
                    poiIndex,
                    settingType: SettingModelSettingType.Connectors,
                    snapPoints: response.snapPoints ?? [],
                    snapRanges: response.snapRanges ?? [],
                    referencePosition: connectorReference?.position,
                    useRay: !connectorReference && state.pois[poiIndex]?.filterType === POIFilterType.Corner
                });
                if (!isActivated) {
                    state.clearManualPlacementData();
                }
            } catch {
                state.clearManualPlacementData();
            } finally {
                usePlacementStore.setState({ waitForPlacementData: false });
            }
        },
        [sessionguid, solveConnectorPlacementMutation]
    );
};
